'use client';

import { useUnit } from 'effector-react';
import { Icon } from '@nx-frontend/shared/ui/icon';
import { generalEnv } from '@nx-frontend/shared/env';
import { useCreateNewTask } from '@nx-frontend/shared/hooks';
import { useCreateNote } from '@nx-frontend/shared/hooks/use-create/use-note-create';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { updateCurrentNoteEvent } from '@nx-frontend/shared/store/note';
import { addNoteEvent } from '@nx-frontend/shared/store/notes';
import { OrgInfoSubscriptionType } from '@nx-frontend/shared/types';
import { AllDocsBtnProps } from '@nx-frontend/shared/types';
import { GetOpenPayWallPopupProps } from '@nx-frontend/shared/types';

interface AllDocsBtnPropsContainer {
  buttonData: AllDocsBtnProps;
  folderId?: string;
  orgId: string;
  subscriptionType: OrgInfoSubscriptionType;
  getOpenPaywallPopup: (props: GetOpenPayWallPopupProps) => void;
}

export function AllDocsButton({ buttonData, folderId }: AllDocsBtnPropsContainer) {
  const [currentWorkspace] = useUnit([$currentWorkspace]);
  const [addNote, updateCurrentNote] = useUnit([addNoteEvent, updateCurrentNoteEvent]);
  const createNote = useCreateNote();
  const createTask = useCreateNewTask();

  const createNewPage = async () => {
    let parentId: string;
    if (!folderId || folderId === 'root' || folderId === 'default') {
      parentId = 'default';
    } else {
      parentId = folderId;
    }

    const note = await createNote({ parentId, workspaceId: currentWorkspace.globalId, redirectAfterCreate: true });

    if (note) {
      addNote(note);
      updateCurrentNote(note);
    }
  };

  const onBtnClick = () => {
    if (buttonData.id === 'newPage') {
      return createNewPage();
    }
    if (buttonData.id === 'newTask') {
      return createTask({
        workspaceId: currentWorkspace.globalId,
        folderId,
      });
    }

    if (buttonData.id === 'template') {
      return window.open(`https://${generalEnv.nimbusDomain}/templates/`);
    }

    if (buttonData.action) {
      buttonData.action();
    }
  };
  return (
    <button
      data-cy={buttonData.title}
      className="flex min-w-[200px] w-full p-3 gap-3 hover:bg-zinc-50 rounded-[6px] border-[1px] border-solid border-border-neutral-200"
      onClick={onBtnClick}
    >
      <Icon icon={buttonData.icon} className="text-cyan-900 !w-5 !h-6 py-0.5 shrink-0" />
      <div className="flex flex-col items-start gap-0.5">
        <span className="text-[14px] font-semibold text-neutral-800 text-left">{buttonData.title}</span>
        <span className="text-[14px] text-neutral-500 text-left">{buttonData.subTitle}</span>
      </div>
    </button>
  );
}
